import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import { Box, Checkbox, FormControlLabel, Grid, Autocomplete } from '@mui/material';
import * as Yup from 'yup';
import styles from './CreateOrganisationSettings.module.scss';
import { organizationSettingsService } from 'core/API/organizationSettings';
import { useOrganizationCrud } from 'core/hooks/useOrganizationCrud';
import useSettingsStore from 'store/useSettingsStore';
import { COUNTRY_LIST, COUNTRY_STATES } from '../constants';

interface CreateOrganisationSettingsProps {
	backButtonClick: () => void;
	onOrganizationCreated?: () => void;
}

const CreateOrganisationSettings: React.FC<CreateOrganisationSettingsProps> = ({
	backButtonClick,
	onOrganizationCreated,
}) => {
	const [apiError, setApiError] = useState<string | null>(null);
	const [apiSuccess, setApiSuccess] = useState<string | null>(null);
	const addOrganization = useSettingsStore((s) => s.addOrganization);

	const { brands, timeZones, fetchBrands } = useOrganizationCrud();

	// Load dropdown data on mount
	useEffect(() => {
		fetchBrands();
	}, [fetchBrands]);

	const initialFormikValues = {
		name: '',
		addressLine1: '',
		city: '',
		postcode: '',
		state: '',
		country: '',
		brand: { label: '' },
		timeZone: { id: 0, label: '' },
		create_predictions: false,
		first_day_of_the_week: '',
	};

	const validationSchema = Yup.object().shape({
		name: Yup.string().required('Organization name is required'),
		addressLine1: Yup.string().required('Street address is required'),
		city: Yup.string().required('City is required'),
		postcode: Yup.string().required('Postal code is required'),
		state: Yup.string().required('State is required'),
		country: Yup.string().required('Country is required'),
	});

	const formik = useFormik({
		initialValues: initialFormikValues,
		validationSchema,
		onSubmit: async (values, { setSubmitting }) => {
			try {
				setApiError(null);
				setApiSuccess(null);

				// Prepare the payload for creating a new organization
				const payload = {
					name: values.name,
					addressLine1: values.addressLine1,
					city: values.city,
					postcode: values.postcode,
					state: values.state,
					country: values.country,
					brand: values.brand,
					timeZone: values.timeZone,
					create_predictions: values.create_predictions,
					first_day_of_the_week: values.first_day_of_the_week,
				};

				// Call the API to create the organization
				const newOrganization = await organizationSettingsService.createOrganization(payload);
				console.log('Organization created successfully:', newOrganization);

				addOrganization(newOrganization);
				setApiSuccess('Organization created successfully!');

				// Call the callback to refresh the organization list
				onOrganizationCreated?.();

				// Navigate back after a short delay
				setTimeout(() => {
					backButtonClick();
				}, 1500);
			} catch (error: any) {
				setApiError(error.message || 'Failed to create organization');
			} finally {
				setSubmitting(false);
			}
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		formik.handleSubmit();
	};

	const getStateOptions = () => {
		if (!formik.values.country) return [];
		return COUNTRY_STATES[formik.values.country] || [];
	};

	return (
		<div className={styles.createOrganizationSetting}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Grid>
						<Button className={styles.backButton} onClick={backButtonClick}>
							Back
						</Button>
					</Grid>
					<Grid item xs={6} display={'flex'} justifyContent="flex-end">
						<Grid>
							<Button
								className={styles.saveButton}
								onClick={handleSubmit}
								disabled={formik.isSubmitting}
							>
								{formik.isSubmitting ? 'Saving...' : 'Save'}
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
					<h2 className={styles.formTitle}>Create New Organization</h2>
				</Grid>
			</Grid>

			{/* API Feedback */}
			{apiSuccess && <div className={styles.successMessage}>{apiSuccess}</div>}
			{apiError && <div className={styles.errorMessage}>{apiError}</div>}

			<form className={styles.formContainer} onSubmit={formik.handleSubmit}>
				{/* Organization Name */}
				<TextField
					label="Organization Name"
					name="name"
					value={formik.values.name}
					onChange={formik.handleChange}
					error={formik.touched.name && Boolean(formik.errors.name)}
					helperText={formik.touched.name && (formik.errors.name as string)}
					className={styles.input}
					margin="normal"
					fullWidth
					required
				/>

				{/* Street Address */}
				<TextField
					label="Street Address"
					name="addressLine1"
					value={formik.values.addressLine1}
					onChange={formik.handleChange}
					error={formik.touched.addressLine1 && Boolean(formik.errors.addressLine1)}
					helperText={formik.touched.addressLine1 && (formik.errors.addressLine1 as string)}
					className={styles.input}
					margin="normal"
					fullWidth
					required
				/>

				{/* Country */}
				<FormControl fullWidth margin="normal">
					<Autocomplete
						options={COUNTRY_LIST}
						getOptionLabel={(option) => option}
						value={formik.values.country || null}
						onChange={(_, newValue) => {
							formik.setFieldValue('country', newValue || '');
							formik.setFieldValue('state', ''); // Reset state when country changes
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Country"
								error={formik.touched.country && Boolean(formik.errors.country)}
								helperText={formik.touched.country && (formik.errors.country as string)}
								required
							/>
						)}
					/>
				</FormControl>

				{/* State */}
				<FormControl fullWidth margin="normal">
					<Autocomplete
						options={getStateOptions()}
						getOptionLabel={(option) => option}
						value={formik.values.state || null}
						onChange={(_, newValue) => {
							formik.setFieldValue('state', newValue || '');
						}}
						disabled={!formik.values.country}
						renderInput={(params) => (
							<TextField
								{...params}
								label="State"
								error={formik.touched.state && Boolean(formik.errors.state)}
								helperText={formik.touched.state && (formik.errors.state as string)}
								required
							/>
						)}
					/>
				</FormControl>

				{/* City */}
				<TextField
					label="City"
					name="city"
					value={formik.values.city}
					onChange={formik.handleChange}
					error={formik.touched.city && Boolean(formik.errors.city)}
					helperText={formik.touched.city && (formik.errors.city as string)}
					className={styles.input}
					margin="normal"
					fullWidth
					required
				/>

				{/* Postal Code */}
				<TextField
					label="Postal Code"
					name="postcode"
					value={formik.values.postcode}
					onChange={formik.handleChange}
					error={formik.touched.postcode && Boolean(formik.errors.postcode)}
					helperText={formik.touched.postcode && (formik.errors.postcode as string)}
					className={styles.input}
					margin="normal"
					fullWidth
					required
				/>

				{/* Brand */}
				<FormControl fullWidth margin="normal">
					<Autocomplete
						options={brands}
						getOptionLabel={(option) => option.label}
						value={formik.values.brand.label ? formik.values.brand : null}
						onChange={(_, newValue) => {
							formik.setFieldValue('brand', newValue || { label: '' });
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Brand"
								error={formik.touched.brand && Boolean(formik.errors.brand)}
								helperText={formik.touched.brand && (formik.errors.brand as string)}
							/>
						)}
					/>
				</FormControl>

				{/* Time Zone */}
				<FormControl fullWidth margin="normal">
					<Autocomplete
						options={timeZones}
						getOptionLabel={(option) => option.label}
						value={formik.values.timeZone.id ? formik.values.timeZone : null}
						onChange={(_, newValue) => {
							formik.setFieldValue('timeZone', newValue || { id: 0, label: '' });
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Time Zone"
								error={formik.touched.timeZone && Boolean(formik.errors.timeZone)}
								helperText={formik.touched.timeZone && (formik.errors.timeZone as string)}
							/>
						)}
					/>
				</FormControl>

				{/* First Day of the Week */}
				<FormControl fullWidth margin="normal">
					<Autocomplete
						options={['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']}
						getOptionLabel={(option) => option}
						value={formik.values.first_day_of_the_week || null}
						onChange={(_, newValue) => {
							formik.setFieldValue('first_day_of_the_week', newValue || '');
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="First Day of the Week"
								error={
									formik.touched.first_day_of_the_week &&
									Boolean(formik.errors.first_day_of_the_week)
								}
								helperText={
									formik.touched.first_day_of_the_week &&
									(formik.errors.first_day_of_the_week as string)
								}
							/>
						)}
					/>
				</FormControl>

				{/* Checkboxes */}
				<Box sx={{ mt: 2 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={formik.values.create_predictions}
								onChange={formik.handleChange}
								name="create_predictions"
							/>
						}
						label="Create Predictions"
					/>
				</Box>
			</form>
		</div>
	);
};

export default CreateOrganisationSettings;
